import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMobile } from '../../hooks/useMobile';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ResponsiveNavigation - Adaptive navigation component
 * - Hamburger menu on mobile
 * - Horizontal tabs on desktop
 * - Bottom navigation option for mobile
 */
const ResponsiveNavigation = ({
  items = [],
  logo,
  userInfo,
  onSignOut,
  variant = 'header' // 'header' | 'bottom' | 'sidebar'
}) => {
  const { isMobile, isTablet, isBelow } = useMobile();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Show mobile menu for screens below md breakpoint (768px)
  const showMobileMenu = isBelow('md');

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  if (variant === 'bottom' && showMobileMenu) {
    return <BottomNavigation items={items} />;
  }

  if (variant === 'sidebar') {
    return <SidebarNavigation items={items} isOpen={isMenuOpen} onClose={closeMenu} />;
  }

  return (
    <header className="bg-white shadow-sm border-b relative z-50 pt-safe">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            {logo}
          </div>

          {/* Desktop Navigation */}
          {!showMobileMenu && (
            <nav className="flex space-x-8">
              {items.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  isActive={location.pathname === item.path}
                />
              ))}
            </nav>
          )}

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* User info on desktop */}
            {!showMobileMenu && userInfo && (
              <div className="flex items-center gap-2 text-gray-600">
                {userInfo}
              </div>
            )}

            {/* Settings icon on desktop */}
            {!showMobileMenu && (
              <Link
                to="/profile"
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Settings"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </Link>
            )}

            {/* Sign out on desktop */}
            {!showMobileMenu && onSignOut && (
              <button
                onClick={onSignOut}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm">Sign Out</span>
              </button>
            )}

            {/* Mobile hamburger menu */}
            {showMobileMenu && (
              <button
                onClick={toggleMenu}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay 
        isOpen={isMenuOpen} 
        onClose={closeMenu}
        items={items}
        userInfo={userInfo}
        onSignOut={onSignOut}
      />
    </header>
  );
};

/**
 * NavItem - Individual navigation item
 */
const NavItem = ({ item, isActive, onClick }) => {
  const baseClasses = "flex items-center gap-2 px-3 py-4 border-b-2 text-sm font-medium transition-colors";
  const activeClasses = "border-blue-500 text-blue-600";
  const inactiveClasses = "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";

  const content = (
    <>
      {item.icon && <span className="w-4 h-4">{item.icon}</span>}
      {item.label}
      {item.badge && (
        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
          {item.badge}
        </span>
      )}
    </>
  );

  const className = `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;

  if (item.external) {
    return (
      <a href={item.path} className={className} onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <Link to={item.path} className={className} onClick={onClick}>
      {content}
    </Link>
  );
};

/**
 * MobileMenuOverlay - Full screen mobile menu
 */
const MobileMenuOverlay = ({ isOpen, onClose, items, userInfo, onSignOut }) => {
  const location = useLocation();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-white pt-safe pb-safe"
        >
          <div className="flex flex-col h-full">
            {/* Menu items */}
            <nav className="flex-1 pt-4 pb-8 overflow-y-auto">
              <div className="px-4 space-y-1">
                {items.map((item) => (
                  <MobileNavItem 
                    key={item.id} 
                    item={item} 
                    isActive={location.pathname === item.path}
                    onClick={onClose}
                  />
                ))}
              </div>
            </nav>

            {/* User info, settings, and sign out */}
            <div className="border-t border-gray-200 p-4 space-y-4">
              {userInfo && (
                <div className="flex items-center gap-3 text-gray-600">
                  {userInfo}
                </div>
              )}
              
              <Link
                to="/profile"
                onClick={onClose}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </Link>
              
              {onSignOut && (
                <button
                  onClick={() => {
                    onSignOut();
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * MobileNavItem - Mobile navigation item
 */
const MobileNavItem = ({ item, isActive, onClick }) => {
  const baseClasses = "flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors";
  const activeClasses = "bg-blue-50 text-blue-600";
  const inactiveClasses = "text-gray-600 hover:bg-gray-50";

  const content = (
    <>
      {item.icon && <span className="w-5 h-5">{item.icon}</span>}
      <span className="flex-1">{item.label}</span>
      {item.badge && (
        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
          {item.badge}
        </span>
      )}
    </>
  );

  const className = `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;

  if (item.external) {
    return (
      <a href={item.path} className={className} onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <Link to={item.path} className={className} onClick={onClick}>
      {content}
    </Link>
  );
};

/**
 * BottomNavigation - Mobile bottom navigation
 */
const BottomNavigation = ({ items }) => {
  const location = useLocation();

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-safe">
      <div className="grid grid-cols-2 sm:grid-cols-4">
        {items.slice(0, 4).map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-2 py-3 text-xs font-medium transition-colors ${
                isActive 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {item.icon && <span className="w-5 h-5">{item.icon}</span>}
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ResponsiveNavigation;